import * as React from 'react';
import { connect } from 'react-redux';
import { Button, FormControl, FormGroup, ControlLabel, Checkbox } from 'react-bootstrap';
import { Translate } from '../../../shared/LanguageService';
import * as productActions from '../actions/ProductActions';
import CreateProductModal from '../create/CreateProductModal';
import EditProductModal from '../edit/EditProductModal';
import FormSelect from '../../../components/common/FormSelect';
import {
    IProduct,
    INamedIdentity,
    IProductProperty,
    ISite,
    IProductType,
    IProductDeliveryTimes
} from '../Types';
import LogsModal from '../../logs/LogsModal';
import ProductList from './ProductList';
import * as ProductSelector from './../../../store/selectors/ProductsSelectors.store';

interface IState {
    searchFilter: string;
    typeFilter: string;
    showDiscontinued: boolean;
    showEditProductModal: boolean;
    showCreateProductModal: boolean;
    showProductLogsModal: boolean;
    editProduct: IProduct;
    showLogsForId: number;
}

interface IProps {
    actions: IActions;
    products: IProduct[];
    productTypes: IProductType[];
    productProperties: IProductProperty[];
    productDeliveryTimes: IProductDeliveryTimes[];
    bookingConditions: INamedIdentity[];
    legalEntities: INamedIdentity[];
    invoiceProductTypes: INamedIdentity[];
    sites: ISite[];
    site: string;
}

class ProductsPage extends React.Component<IProps, IState> {
    constructor(props: IProps, context: any) {
        super(props, context);

        this.state = {
            searchFilter: '',
            typeFilter: undefined,
            showDiscontinued: false,
            showEditProductModal: false,
            showCreateProductModal: false,
            showProductLogsModal: false,
            editProduct: null,
            showLogsForId: 0
        };

        this.search = this.search.bind(this);
        this.filterByType = this.filterByType.bind(this);
        this.closeEditProductModal = this.closeEditProductModal.bind(this);
        this.createProduct = this.createProduct.bind(this);
        this.closeCreateProductModal = this.closeCreateProductModal.bind(this);
        this.editProduct = this.editProduct.bind(this);
        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.viewLogs = this.viewLogs.bind(this);
    }

    componentDidMount() {
        this.props.actions.loadProducts();
    }

    render() {
        let products = this.props.products;

        if (this.state.searchFilter.length > 0) {
            let lowerCaseSearchFilter = this.state.searchFilter.toLowerCase();

            products = this.props.products.filter((product: IProduct) => {
                return (
                    product.name.toLowerCase().indexOf(lowerCaseSearchFilter) > -1 ||
                    product.description.toLowerCase().indexOf(lowerCaseSearchFilter) > -1
                );
            });
        }

        let typeFilter = this.state.typeFilter;
        if (typeFilter) {
            products = products.filter((product: IProduct) => {
                return product.type.id.toString() === typeFilter;
            });
        }

        let showDiscontinued = this.state.showDiscontinued;
        if (!showDiscontinued) {
            products = products.filter((product: IProduct) => {
                return !product.isDiscontinued;
            });
        }

        return (
            <div>
                <h1>{Translate('products/page/title/products')}</h1>
                <div className="row">
                    <div className="col-md-3">
                        <FormGroup controlId="search_box">
                            <ControlLabel>
                                {Translate('products/page/input-label/search')}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="search"
                                value={this.state.searchFilter ? this.state.searchFilter : ''}
                                onChange={this.search}
                            />
                        </FormGroup>
                    </div>
                    <div className="col-md-3">
                        <FormGroup controlId="search_filter">
                            <ControlLabel>
                                {Translate('products/page/select-label/filter')}
                            </ControlLabel>
                            <FormSelect
                                name="filter"
                                value={this.state.typeFilter}
                                onChange={this.filterByType}
                            >
                                <option value="-1">
                                    {Translate('products/page/select-label/filter')}
                                </option>
                                {this.props.productTypes.map((t: INamedIdentity) => {
                                    return (
                                        <option key={t.id} value={t.id.toString()}>
                                            {t.name}
                                        </option>
                                    );
                                })}
                            </FormSelect>
                        </FormGroup>
                    </div>
                    <div className="col-md-3 no-label">
                        <FormGroup>
                            <Checkbox
                                className=""
                                name="showDiscontinued"
                                checked={this.state.showDiscontinued}
                                onChange={this.onCheckboxChange}
                            >
                                <span>
                                    {Translate('products/page/input-label/include-discontinued')}
                                </span>
                            </Checkbox>
                        </FormGroup>
                    </div>
                    <div className="col-md-3 no-label">
                        <Button
                            className="pull-right"
                            onClick={this.createProduct}
                            bsSize="large"
                            bsStyle="success"
                        >
                            {Translate('products/page/button-label/create-new-product')}
                        </Button>
                    </div>
                </div>
                <ProductList
                    products={products}
                    editProduct={this.editProduct}
                    viewLogs={this.viewLogs}
                    parent={this}
                />

                {this.renderModal()}
            </div>
        );
    }

    renderModal() {
        if (this.state.showCreateProductModal) {
            return (
                <CreateProductModal
                    productTypes={this.props.productTypes}
                    productProperties={this.props.productProperties}
                    productDeliveryTimes={this.props.productDeliveryTimes}
                    bookingConditions={this.props.bookingConditions}
                    legalEntities={this.props.legalEntities}
                    invoiceProductTypes={this.props.invoiceProductTypes}
                    sites={this.props.sites}
                    close={this.closeCreateProductModal}
                />
            );
        }

        if (this.state.showEditProductModal) {
            return (
                <EditProductModal
                    product={this.state.editProduct}
                    close={this.closeEditProductModal}
                    productTypes={this.props.productTypes}
                    productProperties={this.props.productProperties}
                    productDeliveryTimes={this.props.productDeliveryTimes}
                    bookingConditions={this.props.bookingConditions}
                    legalEntities={this.props.legalEntities}
                    invoiceProductTypes={this.props.invoiceProductTypes}
                    sites={this.props.sites}
                    site={this.props.site}
                />
            );
        }

        if (this.state.showProductLogsModal) {
            return (
                <LogsModal
                    id={this.state.showLogsForId}
                    type="ProductId"
                    shouldShowModal={this.state.showProductLogsModal}
                    closeModal={() =>
                        this.setState({
                            showProductLogsModal: false,
                            showLogsForId: 0
                        } as IState)
                    }
                />
            );
        }

        return null;
    }

    createProduct() {
        this.setState({ showCreateProductModal: true } as IState);
    }

    closeEditProductModal() {
        this.setState({ showEditProductModal: undefined } as IState);
    }

    closeCreateProductModal() {
        this.setState({ showCreateProductModal: undefined } as IState);
    }

    editProduct(id: any) {
        let product = this.props.products.find((item: IProduct) => {
            return item.id === id;
        });

        this.setState({
            showEditProductModal: true,
            editProduct: product
        } as IState);
    }

    viewLogs(id: number) {
        this.setState({
            showProductLogsModal: true,
            showLogsForId: id
        } as IState);
    }

    search(e: any) {
        return this.setState({ searchFilter: e.target.value } as IState);
    }

    filterByType(e: any) {
        if (e.target.value === '-1') {
            return this.setState({ typeFilter: undefined } as IState);
        }

        return this.setState({ typeFilter: e.target.value } as IState);
    }

    onCheckboxChange() {
        this.setState({
            showDiscontinued: !this.state.showDiscontinued
        } as IState);
    }
}

function mapStateToProps(state: any) {
    return {
        products: state.productsResult.products,
        productTypes: state.productsResult.productTypes,
        productProperties: state.productsResult.properties,
        productDeliveryTimes: ProductSelector.getProductDeliveryTimes(state.productsResult),
        bookingConditions: state.productsResult.bookingConditions,
        legalEntities: state.productsResult.legalEntities,
        invoiceProductTypes: state.productsResult.invoiceProductTypes,
        sites: state.productsResult.sites,
        site: state.site
    };
}

interface IActions {
    loadProducts: () => Promise<any>;
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            loadProducts: () => dispatch(productActions.getProducts())
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductsPage);
